/**
 * PageGuard — wraps every protected route.
 *   1. Resolves the current location to a registry page. No view permission (or page not in the registry
 *      at all, for non-super staff) → back to /dashboard + toast.
 *   2. While the page is mounted, hides edit / delete controls when the role lacks that permission.
 *      Detection: buttons/links containing a lucide edit/plus/save or trash icon, or whose own text starts/ends with
 *      تعديل / إضافة / حفظ / Edit / Add / Save / حذف / Delete, or carrying data-perm="edit|delete".
 *      Pages marked `data-perm-scope` keep their own wrapper; everything else is scanned from <body>.
 */
import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { usePerms, isAlwaysAllowed } from './PermissionsContext';
import { matchPage } from './registry';

const EDIT_ICONS = ['lucide-edit-3', 'lucide-edit-2', 'lucide-edit', 'lucide-pencil', 'lucide-square-pen', 'lucide-pen-line', 'lucide-pen-square', 'lucide-file-pen', 'lucide-pencil-line', 'lucide-plus', 'lucide-plus-circle', 'lucide-circle-plus', 'lucide-save', 'lucide-user-plus', 'lucide-file-plus'];
const DELETE_ICONS = ['lucide-trash-2', 'lucide-trash'];
const EDIT_WORDS = ['تعديل', 'edit', 'تحرير', 'إضافة', 'اضافة', 'add', 'جديد', 'new', 'حفظ', 'save', 'إنشاء', 'create'];
const DELETE_WORDS = ['حذف', 'delete', 'إزالة', 'remove'];
const CLICKABLE = 'button, a[href], [role="button"], [role="menuitem"]';

const ownText = (el) => (el.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ');
const hasIcon = (el, names) => names.some((n) => el.querySelector(`svg.${n}`) || el.classList.contains(n));
const wordMatch = (txt, words) => words.some((w) => txt === w || txt.startsWith(`${w} `) || txt.endsWith(` ${w}`));

function classify(el) {
  const perm = el.getAttribute('data-perm');
  if (perm === 'edit' || perm === 'delete') return perm;
  if (el.closest('[data-perm-ignore]')) return null;
  if (hasIcon(el, DELETE_ICONS)) return 'delete';
  if (hasIcon(el, EDIT_ICONS)) return 'edit';
  const txt = ownText(el);
  if (txt && txt.length <= 24) {
    if (wordMatch(txt, DELETE_WORDS)) return 'delete';
    if (wordMatch(txt, EDIT_WORDS)) return 'edit';
  }
  return null;
}

function applyHiding(root, { hideEdit, hideDelete }) {
  root.querySelectorAll(CLICKABLE).forEach((el) => {
    const kind = classify(el);
    if (!kind) return;
    if ((kind === 'edit' && hideEdit) || (kind === 'delete' && hideDelete)) {
      if (el.style.display !== 'none') { el.dataset.permHidden = kind; el.style.display = 'none'; }
    }
  });
}

export default function PageGuard({ children }) {
  const location = useLocation();
  const perms = usePerms();
  const node = matchPage(location.pathname, location.search);
  const always = isAlwaysAllowed(location.pathname);
  // deny-by-default: non-super staff may open only registered pages they can view (or staff self-service pages)
  const allowed = always || perms.isSuper || (node ? perms.canView(node.id) : false);
  const hideEdit = Boolean(node) && !perms.canEdit(node.id);
  const hideDelete = Boolean(node) && !perms.canDelete(node.id);
  const toasted = useRef(false);

  useEffect(() => {
    if (!allowed && !toasted.current) {
      toasted.current = true;
      toast.error('ليس لديك صلاحية الدخول إلى هذه الصفحة');
    }
  }, [allowed]);

  useEffect(() => {
    if (perms.isSuper || (!hideEdit && !hideDelete)) return undefined;
    const root = document.body;
    root.dataset.permNoEdit = hideEdit ? '1' : '';
    root.dataset.permNoDelete = hideDelete ? '1' : '';
    applyHiding(root, { hideEdit, hideDelete });
    let raf = 0; // coalesce bursts of DOM mutations into one scan per frame
    const obs = new MutationObserver((list) => {
      if (!list.some((m) => m.addedNodes.length)) return;
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; applyHiding(root, { hideEdit, hideDelete }); });
    });
    obs.observe(root, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
      delete root.dataset.permNoEdit; delete root.dataset.permNoDelete;
      root.querySelectorAll('[data-perm-hidden]').forEach((el) => { el.style.display = ''; delete el.dataset.permHidden; });
    };
  }, [hideEdit, hideDelete, perms.isSuper, location.pathname, location.search]);

  if (!allowed) return <Navigate to="/dashboard" replace />;
  return children;
}
